import { AddTask } from "@mui/icons-material";
import { alpha, Box, Button, Card, CardContent, CardHeader, Divider, Stack, styled, Typography } from "@mui/material";
import _, { Dictionary } from "lodash";
import { DateTime } from "luxon";
import { useCallback, useEffect, useState } from "react";
import {
  AccordionViewList,
  CreateTaskItemModal,
  filterTaskItemsFn,
  sortTaskItemsFn,
  TaskItem,
  TaskItemListItemSkeleton,
} from "../../../../components";
import {
  ClosedTaskItemStatuses,
  TASK_ITEM_STATUSES,
  TaskItemsFilterByOption,
  TeamTaskItemsGroupByOption,
} from "../../../../constants";
import { useSession } from "../../../../hooks";
import { TaskItemDto, useGetTeam, useGetTeamTaskItemsTree } from "../../../../http";
import { useTeamImplementationSettings } from "../hooks";
import { TeamTaskItemsFilterButton } from "./TeamTaskItemsFilterButton";
import { TeamTaskItemsTable } from "./TeamTaskItemsTable";

const AccordionContainer = styled(Box)`
  border-style: dashed;
  border-color: ${({ theme }) => alpha(theme.palette.common.black, 0.3)};
  border-width: 1px;
  border-radius: 10px;
`;

interface IProps {
  teamSlug: string;
  isTableView: boolean;
}

const TeamTaskItemsList = (props: IProps) => {
  const { displayName: sessionUserDisplayName, userId: sessionUserGuid } = useSession();
  const { settings } = useTeamImplementationSettings(props.teamSlug);
  const { data: team, isLoading: isLoadingTeam } = useGetTeam(props.teamSlug, sessionUserGuid);
  const { data: tasks, isLoading: isLoadingTaskItems, fetchTaskItemSubtasks } = useGetTeamTaskItemsTree(props.teamSlug);
  const [groupedTasks, setGroupedTasks] = useState<Dictionary<TaskItemDto[]> | undefined>(undefined);
  const [isCreateTaskItemModalOpen, setIsCreateTaskItemModalOpen] = useState(false);

  const groupByFunction = useCallback(
    (item: TaskItemDto) => {
      switch (settings.taskItemsGroupByOption) {
        case TeamTaskItemsGroupByOption.None:
          return "All Tasks";
        case TeamTaskItemsGroupByOption.DueDate: {
          if (item.dueDate == null) {
            return "No Due Date";
          }

          const dueDate = DateTime.fromISO(item.dueDate);

          if (dueDate < DateTime.now()) {
            return "Past Due";
          } else if (dueDate < DateTime.now().plus({ week: 1 })) {
            return "Due Soon";
          } else {
            return "Due Later";
          }
        }
        case TeamTaskItemsGroupByOption.InFocus:
          return item.owners.some((x) => x.isFocused) ? "Tasks in Focus" : "All Other Tasks";
        case TeamTaskItemsGroupByOption.Owner: {
          if (item.owners.length === 0) {
            return "No Owner";
          }

          return item.owners
            .map((x) => x.user.displayName)
            .sort((a, b) => a.localeCompare(b))
            .join(", ");
        }
        case TeamTaskItemsGroupByOption.PrimaryOwner: {
          if (item.primaryOwner == null) {
            return "No Primary Owner";
          }

          return item.primaryOwner.displayName;
        }
        case TeamTaskItemsGroupByOption.Status: {
          return TASK_ITEM_STATUSES.find((x) => x.id === item.currentStatus)?.name;
        }
        default:
          return;
      }
    },
    [settings.taskItemsGroupByOption]
  );

  const groupSortByFunction = useCallback(
    (item: TaskItemDto) => {
      switch (settings.taskItemsGroupByOption) {
        case TeamTaskItemsGroupByOption.DueDate:
          return item.dueDate;
        case TeamTaskItemsGroupByOption.InFocus:
          let ownerInFocusCount = item.owners.filter((owner) => owner.isFocused).length;
          return -1 * ownerInFocusCount;
        case TeamTaskItemsGroupByOption.None:
          return;
        case TeamTaskItemsGroupByOption.Owner:
          if (item.owners.length === 1 && item.owners[0].user.userId === sessionUserGuid) {
            return -1;
          }

          return item.owners
            .map((x) => x.user.displayName)
            .sort((a, b) => a.localeCompare(b))
            .join(", ");
        case TeamTaskItemsGroupByOption.PrimaryOwner:
          if (item.primaryOwner?.userId === sessionUserGuid) {
            return -1;
          }

          return item.primaryOwner?.displayName;
        case TeamTaskItemsGroupByOption.Status:
          return TASK_ITEM_STATUSES.find((x) => x.id === item.currentStatus)?.sortOrder;
        default:
          return;
      }
    },
    [sessionUserGuid, settings.taskItemsGroupByOption]
  );

  const filterByFunction = useCallback(
    (item: TaskItemDto) => {
      return filterTaskItemsFn(settings.taskItemsFilterByOption, settings.taskItemsFilterByStatusOption, item);
    },
    [settings.taskItemsFilterByOption, settings.taskItemsFilterByStatusOption]
  );

  function sortByFunction(a: TaskItemDto, b: TaskItemDto) {
    return sortTaskItemsFn(settings.taskItemsSortByOption, settings.taskItemsSortByDirection, a, b);
  }

  useEffect(() => {
    if (tasks !== undefined) {
      const taskItems = _.groupBy(_.sortBy(tasks.filter(filterByFunction), groupSortByFunction), groupByFunction);
      setGroupedTasks(taskItems);
    }
  }, [tasks, filterByFunction, groupByFunction, groupSortByFunction]);

  const isLoading = isLoadingTaskItems || isLoadingTeam;

  return (
    <>
      <Card>
        <CardHeader
          title={<Typography variant="h6">Tasks</Typography>}
          action={
            <Stack direction="row" spacing={1} divider={<Divider orientation="vertical" flexItem />}>
              {(team?.isAdministrator || team?.isMember) && (
                <Button
                  variant="tertiary"
                  disabled={isLoading}
                  startIcon={<AddTask />}
                  onClick={() => setIsCreateTaskItemModalOpen(true)}
                >
                  Add New Task
                </Button>
              )}
              {team && <TeamTaskItemsFilterButton teamSlug={team.slug} disabled={isLoading} />}
            </Stack>
          }
        />
        <Divider />
        <CardContent>
          {isLoading || team === undefined || groupedTasks === undefined ? (
            <AccordionContainer>
              <Stack spacing={0.5} sx={{ mx: 1, my: 0.5 }} divider={<Divider variant="dashed" />}>
                {[...Array(3)].map((_, i) => (
                  <TaskItemListItemSkeleton key={i} />
                ))}
              </Stack>
            </AccordionContainer>
          ) : (
            <Stack spacing={1}>
              {_.keys(groupedTasks).length === 0 ? (
                <Typography>Nothing here yet.</Typography>
              ) : (
                _.keys(groupedTasks).map((groupName) => (
                  <AccordionContainer
                    key={groupName}
                    sx={{
                      mb: groupName === sessionUserDisplayName ? "20px !important" : undefined,
                    }}
                  >
                    <AccordionViewList
                      title={groupName}
                      isLoading={isLoading}
                      dataSource={groupedTasks[groupName]}
                      divider={<Divider variant="dashed" sx={{ mx: 1 }} />}
                      renderContent={
                        props.isTableView
                          ? (items) => (
                              <TeamTaskItemsTable
                                tableId={`teamTaskItemsList-${team.slug}`}
                                taskItems={items.sort(sortByFunction)}
                                fetchTaskItemSubtasks={fetchTaskItemSubtasks}
                                enableActions={team.isAdministrator || team.isMember}
                              />
                            )
                          : undefined
                      }
                      renderItem={(item) => (
                        <TaskItem key={item.guid} taskItem={item} userCanEdit={item.userCanEdit} sx={{ px: 1 }} />
                      )}
                      activeItemsListProps={{
                        activeItemsSortFunction: sortByFunction,
                      }}
                      closedItemsListProps={
                        settings.taskItemsFilterByOption !== TaskItemsFilterByOption.Status
                          ? {
                              closedItemsFilter: (taskItem) => ClosedTaskItemStatuses.includes(taskItem.currentStatus),
                              closedItemsSortFunction: sortByFunction,
                            }
                          : undefined
                      }
                    />
                  </AccordionContainer>
                ))
              )}
            </Stack>
          )}
        </CardContent>
      </Card>

      <CreateTaskItemModal
        visible={isCreateTaskItemModalOpen}
        onCreated={() => setIsCreateTaskItemModalOpen(false)}
        onCancelled={() => setIsCreateTaskItemModalOpen(false)}
        defaultSelectedTeamSlug={props.teamSlug}
      />
    </>
  );
};

export { TeamTaskItemsList };
